import { getDemoSupervisorControls } from '../data/inventoryDemoDataset';

const STORAGE_KEY = 'poc-app-inventory-supervisor-control-v1';

export interface SupervisorControlEntry {
  controlePar: string;
}

interface StorageShape {
  byReference: Record<string, Record<string, SupervisorControlEntry>>;
}

const emptyShape = (): StorageShape => ({ byReference: {} });

function readStorage(): StorageShape {
  if (typeof window === 'undefined') {
    return emptyShape();
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return emptyShape();
    }

    const parsed = JSON.parse(raw) as unknown;

    if (!parsed || typeof parsed !== 'object') {
      return emptyShape();
    }

    const obj = parsed as { byReference?: unknown };

    if (!obj.byReference || typeof obj.byReference !== 'object') {
      return emptyShape();
    }

    const byReference: StorageShape['byReference'] = {};

    for (const [ref, val] of Object.entries(obj.byReference as Record<string, unknown>)) {
      if (!val || typeof val !== 'object') {
        byReference[ref] = {};
        continue;
      }

      const map: Record<string, SupervisorControlEntry> = {};

      for (const [lineId, entry] of Object.entries(val as Record<string, unknown>)) {
        if (entry === true) {
          map[lineId] = { controlePar: '' };
        } else if (entry && typeof entry === 'object') {
          const controlePar = (entry as { controlePar?: unknown }).controlePar;

          map[lineId] = {
            controlePar: typeof controlePar === 'string' ? controlePar.trim() : '',
          };
        }
      }

      byReference[ref] = map;
    }

    return { byReference };
  } catch {
    return emptyShape();
  }
}

function writeStorage(data: StorageShape): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
}

export function loadSupervisorControlsForInventory(
  referenceInventaire: string,
): Record<string, SupervisorControlEntry> {
  const data = readStorage();
  const persisted = data.byReference[referenceInventaire] ?? {};
  const demo = getDemoSupervisorControls(referenceInventaire);

  if (!demo) {
    return persisted;
  }

  return { ...demo, ...persisted };
}

export function persistSupervisorControlValide(
  referenceInventaire: string,
  lineId: string,
  controlePar: string,
): void {
  const data = readStorage();
  const existing = data.byReference[referenceInventaire] ?? {};

  data.byReference[referenceInventaire] = {
    ...existing,
    [lineId]: { controlePar },
  };
  writeStorage(data);
}
